import React from "react";
import { Badge, BlockStack, InlineStack, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import PrettyDate from "components/format/PrettyDate";

export type SignerStatus = "signed" | "rejected" | "withdrawn" | "pending";

/** A signer's status string from the server as the list shows it; a status the list has no badge for counts as waiting. */
export function signerStatusOf(status: string): SignerStatus {
  return status === "signed" || status === "rejected" || status === "withdrawn" ? status : "pending";
}

export type SignerRowData = {
  name: string;
  date: string;
  status: SignerStatus;
  /** Shown as text under the name of a signer who declined: a tooltip does not open on touch. */
  declineReason?: string;
  /** This signer's buttons, laid out as a column on phones so each one is easy to hit. */
  actions?: React.ReactNode;
};

/** One stacked row per signer: name and status on top, date under them, buttons last. */
export default function SignerList({ rows }: { rows: SignerRowData[] }) {
  const { t } = useTranslation();

  return (
    <BlockStack gap="0">
      {rows.map((row, idx) => (
        <div key={idx} className="py-3 border-b border-gray-200 last:border-b-0">
          <BlockStack gap="100">
            <InlineStack align="space-between" blockAlign="center" wrap={false}>
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {row.name}
              </Text>
              {row.status === "signed" && <Badge tone="success">{t("signed")}</Badge>}
              {row.status === "rejected" && <Badge tone="critical">{t("declined")}</Badge>}
              {row.status === "withdrawn" && <Badge>{t("signer_status_withdrawn")}</Badge>}
              {row.status === "pending" && <Badge tone="attention">{t("pending")}</Badge>}
            </InlineStack>
            {row.status === "rejected" && row.declineReason && (
              <Text as="span" variant="bodySm" tone="critical">
                {row.declineReason}
              </Text>
            )}
            <Text as="span" variant="bodySm" tone="subdued">
              <PrettyDate dateString={row.date} showTime={true} />
            </Text>
            {row.actions && (
              <div className="flex flex-wrap gap-2 max-md:flex-col">{row.actions}</div>
            )}
          </BlockStack>
        </div>
      ))}
    </BlockStack>
  );
}
